import { useCallbackRef } from '@synoptic/ui-kit/utils/use-callback-ref.tsx';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { PostCardMediaFallback } from '@/components/post-card/post-card-media/post-card-media-item-fallback.tsx';
import { S3UserVideo } from '@/components/s3/user/s3-video';
import { useInterval } from '@/utils/use-interval.ts';
import { S3UserImg } from '../../s3/user/s3-img';
import {
  expandedMediaMaxHeight,
  expandedMediaMaxWidth,
  postCardMediaExpandedImg,
  postCardMediaExpandedVideo,
  postCardMediaImg,
  postCardMediaVideo,
} from './post-card-media.css';

/**
 * Polling strategy:
 * - First 60 attempts poll every 1 sec
 * - Then 4 attempts poll every 1 min
 * - Max duration: 5 minutes
 * - Do not poll if post older than 1 day
 * */

const POLL_INTERVAL_FAST = 1000;
const POLL_INTERVAL_SLOW = 1000 * 60;
const FAST_ATTEMPTS_LIMIT = 60;
const SLOW_ATTEMPTS_LIMIT = 4;
const MAX_ATTEMPTS = FAST_ATTEMPTS_LIMIT + SLOW_ATTEMPTS_LIMIT;
const MAX_POST_AGE_TO_POLL = 24; // in hours

const getPollingInterval = (attempt: number): number | null => {
  if (attempt <= FAST_ATTEMPTS_LIMIT) return POLL_INTERVAL_FAST;
  if (attempt <= MAX_ATTEMPTS) return POLL_INTERVAL_SLOW;
  return null;
};

const appendCacheKey = (s3Key: string, n: number) => {
  if (n <= 0) return s3Key;
  return s3Key.includes('?') ? `${s3Key}&ck=${n}` : `${s3Key}?ck=${n}`;
};

const getDiffHours = (createdAt?: string) => {
  const now = new Date();
  const mediaCreatedAt = createdAt ? new Date(createdAt) : now;
  return (now.getTime() - mediaCreatedAt.getTime()) / (1000 * 60 * 60);
};

export const PostCardMediaItem = ({
  s3Key,
  userId,
  type,
  expanded,
  w = expanded ? expandedMediaMaxWidth : undefined,
  h = expanded ? expandedMediaMaxHeight : undefined,
  createdAt,
  isPreSignedUrl,
}: {
  s3Key: string;
  userId: string;
  type: string;
  expanded?: boolean;
  w?: number;
  h?: number;
  createdAt?: string;
  isPreSignedUrl?: boolean;
}) => {
  const [showFallback, setShowFallback] = useState(isPreSignedUrl);
  const [polling, setPolling] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useInterval(
    () => {
      if (attempt >= MAX_ATTEMPTS) {
        setPolling(false);
        setShowFallback(false);
        return;
      }

      setAttempt((a) => a + 1);
    },
    polling ? getPollingInterval(attempt) : null,
  );

  const onError = useCallbackRef(() => {
    const diffHours = getDiffHours(createdAt);

    if (isPreSignedUrl && diffHours < MAX_POST_AGE_TO_POLL) {
      setPolling(true);
      return;
    }

    setShowFallback(false);
    setPolling(false);
  });

  const onLoad = useCallbackRef(() => {
    setPolling(false);
    setShowFallback(false);
  });

  const mediaStyle: React.CSSProperties | undefined = useMemo(
    () =>
      showFallback
        ? {
            visibility: 'hidden',
            width: 0,
            height: 0,
          }
        : undefined,
    [showFallback],
  );

  const bustedS3Key = useMemo(
    () => appendCacheKey(s3Key, attempt),
    [s3Key, attempt],
  );

  return (
    <>
      {showFallback ? (
        <PostCardMediaFallback
          label={polling ? 'Waiting for author to upload media...' : undefined}
        />
      ) : null}

      {type.startsWith('video/') ? (
        <S3UserVideo
          key={`${s3Key}-${attempt}`}
          preload={'metadata'}
          s3Key={bustedS3Key}
          controls
          playsInline
          userId={userId}
          className={clsx(
            postCardMediaVideo,
            expanded && postCardMediaExpandedVideo,
          )}
          onClick={expanded ? (e) => e.stopPropagation() : undefined}
          onLoadedData={onLoad}
          onError={onError}
          style={mediaStyle}
        />
      ) : (
        <S3UserImg
          key={`${s3Key}-${attempt}`}
          s3Key={bustedS3Key}
          loading={expanded ? 'eager' : 'lazy'}
          userId={userId}
          className={clsx(
            postCardMediaImg,
            expanded && postCardMediaExpandedImg,
          )}
          w={w}
          h={h}
          onLoad={onLoad}
          onError={onError}
          style={mediaStyle}
        />
      )}
    </>
  );
};
